import { useState, useEffect, useMemo, ReactNode } from "react";
import { CircularProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";

import * as Database from "utils/database.utils";
import { cyclicalityOptions, gicsSectorOptions } from "assets/data/lists";

import Table from "components/Common/Table/Table";
import TableCell from "components/Common/TableCell/TableCell";
import HistoryTabContainer from "./HistoryTab.styles";

type Props = {
    companyId: string;
};

type SelectOption = {
    label: string;
    value: string;
};

type HistoryRecord = {
    id: number;
    ticker: string;
    name: string;
    team_id: number | null;
    analyst_id: string | null;
    isin: string | null;
    sedol: string | null;
    cusip: string | null;
    eu_composite_ticker: string | null;
    sector_risk: string | null;
    genim_gics_sector: string | null;
    cyclicality: string | null;
    gef_max_wgt_adj: number | null;
    gef_focus_list: boolean;
    aef_focus_list: boolean;
    packaged_software: boolean;
    med_tools: boolean;
    ipo_flag: boolean;
    private_flag: boolean;
    valid_from: string;
    updated_by: string | null;
};

type ChangedFields = Set<keyof HistoryRecord>;

export default function HistoryTab({ companyId }: Props) {
    const [loading, setLoading] = useState(true);
    const [historyData, setHistoryData] = useState<HistoryRecord[]>([]);
    const [analystOptions, setAnalystOptions] = useState<SelectOption[]>([]);
    const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [historyRes, analystsRes, teamsRes] = await Promise.all([
                    Database.get(`api/company/${companyId}/history`),
                    Database.get("api/company/analysts"),
                    Database.get("api/company/teams?usedInCompanyManagement=true"),
                ]);

                if (historyRes.ok) {
                    const historyJson = await historyRes.json();
                    setHistoryData(historyJson.data || []);
                }

                if (analystsRes.ok) {
                    const analystsJson = await analystsRes.json();
                    setAnalystOptions(
                        analystsJson.data.map(
                            (item: { name: string; id: string }) => ({
                                label: item.name,
                                value: String(item.id),
                            })
                        )
                    );
                }

                if (teamsRes.ok) {
                    const teamsJson = await teamsRes.json();
                    setTeamOptions(
                        teamsJson.data.map(
                            (item: { id: number; name: string }) => ({
                                label: item.name,
                                value: String(item.id),
                            })
                        )
                    );
                }
            } catch (err) {
                console.error("Error fetching history data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [companyId]);

    const getLabel = (
        options: SelectOption[],
        value: string | null
    ): string => {
        if (!value) return "-";
        return options.find((o) => o.value === value)?.label || value;
    };

    const formatPercentage = (value: number | null): string => {
        if (value === null || value === undefined) return "-";
        return `${value}%`;
    };

    const changedFieldsMap = useMemo(() => {
        const map = new Map<number, ChangedFields>();
        const comparableFields: (keyof HistoryRecord)[] = [
            "ticker",
            "name",
            "team_id",
            "analyst_id",
            "isin",
            "sedol",
            "cusip",
            "eu_composite_ticker",
            "sector_risk",
            "genim_gics_sector",
            "cyclicality",
            "gef_max_wgt_adj",
            "gef_focus_list",
            "aef_focus_list",
            "packaged_software",
            "med_tools",
            "ipo_flag",
            "private_flag",
        ];

        for (let i = 0; i < historyData.length - 1; i++) {
            const current = historyData[i];
            const previous = historyData[i + 1];
            const changed = new Set<keyof HistoryRecord>();

            for (const field of comparableFields) {
                if (current[field] !== previous[field]) {
                    changed.add(field);
                }
            }

            map.set(current.id, changed);
        }

        return map;
    }, [historyData]);

    const HighlightCell = ({
        row,
        field,
        children,
    }: {
        row: HistoryRecord;
        field: keyof HistoryRecord;
        children: ReactNode;
    }) => {
        const isChanged = changedFieldsMap.get(row.id)?.has(field);
        return isChanged ? (
            <span className="changedCell">{children}</span>
        ) : (
            <>{children}</>
        );
    };

    const createColumn = (
        id: number,
        title: string,
        field: keyof HistoryRecord,
        render: (row: HistoryRecord) => ReactNode
    ) => ({
        id,
        title,
        sortable: false,
        value: field,
        fullDataRow: true,
        cell: (row: HistoryRecord) => (
            <HighlightCell row={row} field={field}>
                {render(row)}
            </HighlightCell>
        ),
        show: true,
    });

    if (loading) {
        return (
            <HistoryTabContainer>
                <div className="loadingContainer">
                    <CircularProgress size={40} />
                </div>
            </HistoryTabContainer>
        );
    }

    if (historyData.length === 0) {
        return (
            <HistoryTabContainer>
                <div className="errorContainer">
                    <p>No history records found</p>
                </div>
            </HistoryTabContainer>
        );
    }

    const renderCheck = (value: boolean) =>
        value ? (
            <FontAwesomeIcon
                className="checkIcon"
                icon={faCheckCircle}
                fixedWidth
            />
        ) : (
            <span>-</span>
        );

    const columns = [
        {
            id: 1,
            title: "VALID FROM",
            sortable: false,
            value: "valid_from",
            cell: (data: string) => <TableCell data={data || "-"} />,
            show: true,
        },
        {
            id: 2,
            title: "UPDATED BY",
            sortable: false,
            value: "updated_by",
            cell: (data: string) => <TableCell data={data || "-"} />,
            show: true,
        },
        createColumn(3, "TICKER", "ticker", (row) => (
            <TableCell data={row.ticker || "-"} />
        )),
        createColumn(4, "COMPANY NAME", "name", (row) => (
            <TableCell data={row.name || "-"} />
        )),
        createColumn(5, "TEAM", "team_id", (row) => (
            <TableCell
                data={getLabel(
                    teamOptions,
                    row.team_id ? String(row.team_id) : null
                )}
            />
        )),
        createColumn(6, "ANALYST", "analyst_id", (row) => (
            <TableCell data={getLabel(analystOptions, row.analyst_id)} />
        )),
        createColumn(7, "ISIN", "isin", (row) => (
            <TableCell data={row.isin || "-"} />
        )),
        createColumn(8, "SEDOL ID", "sedol", (row) => (
            <TableCell data={row.sedol || "-"} />
        )),
        createColumn(9, "CUSIP ID", "cusip", (row) => (
            <TableCell data={row.cusip || "-"} />
        )),
        createColumn(10, "EU COMPOSITE TICKER", "eu_composite_ticker", (row) => (
            <TableCell data={row.eu_composite_ticker || "-"} />
        )),
        createColumn(11, "SECTOR/RISK", "sector_risk", (row) => (
            <TableCell data={row.sector_risk || "-"} />
        )),
        createColumn(12, "GenIM GICS SECTOR", "genim_gics_sector", (row) => (
            <TableCell
                data={getLabel(gicsSectorOptions, row.genim_gics_sector)}
            />
        )),
        createColumn(13, "CYCLICALITY", "cyclicality", (row) => (
            <TableCell data={getLabel(cyclicalityOptions, row.cyclicality)} />
        )),
        createColumn(14, "GEF MAX WGT ADJ", "gef_max_wgt_adj", (row) => (
            <TableCell data={formatPercentage(row.gef_max_wgt_adj)} />
        )),
        createColumn(15, "GEF FL", "gef_focus_list", (row) =>
            renderCheck(row.gef_focus_list)
        ),
        createColumn(16, "AEF FL", "aef_focus_list", (row) =>
            renderCheck(row.aef_focus_list)
        ),
        createColumn(17, "PACKAGED SOFTWARE", "packaged_software", (row) =>
            renderCheck(row.packaged_software)
        ),
        createColumn(18, "MED TOOLS", "med_tools", (row) =>
            renderCheck(row.med_tools)
        ),
        createColumn(19, "IPO", "ipo_flag", (row) => renderCheck(row.ipo_flag)),
        createColumn(20, "PRIVATE", "private_flag", (row) =>
            renderCheck(row.private_flag)
        ),
    ];

    const tableData = historyData.map((record) => ({
        ...record,
        tableID: record.id,
    }));

    return (
        <HistoryTabContainer>
            <Table
                data={tableData}
                columns={columns}
                filterQuery=""
                showHeader
            />
        </HistoryTabContainer>
    );
}
